import { Component } from '@theme/component';

class TabbedContent extends Component {
  requiredRefs = ['tablist'];
  #touchStartX = 0;
  #touchStartY = 0;

  connectedCallback() {
    super.connectedCallback();

    const { tablist, tabs } = this.refs;
    if (tablist && tabs) {
      for (const tab of tabs) {
        tablist.appendChild(tab);
        tab.hidden = false;
      }
    }

    this.addEventListener('keydown', this.#handleKeydown);
    this.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('touchstart', this.#handleTouchStart);
    this.removeEventListener('touchend', this.#handleTouchEnd);
  }

  selectTab(event) {
    const tab = event.target.closest('[role="tab"]');
    const index = this.refs.tabs?.indexOf(tab);
    if (index == null || index < 0) return;
    this.#activate(index);
  }

  #activate(index) {
    const { tabs, panels } = this.refs;
    if (!tabs || !panels) return;

    for (const [i, tab] of tabs.entries()) {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    }

    for (const [i, panel] of panels.entries()) {
      panel.toggleAttribute('hidden', i !== index);
    }

    tabs[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }

  #activeIndex() {
    return this.refs.tabs?.findIndex((t) => t.getAttribute('aria-selected') === 'true') ?? 0;
  }

  #handleTouchStart = (event) => {
    if (event.target?.closest('[role="tablist"]')) return;
    this.#touchStartX = event.touches[0].clientX;
    this.#touchStartY = event.touches[0].clientY;
  };

  #handleTouchEnd = (event) => {
    if (event.target?.closest('[role="tablist"]')) return;
    const dx = event.changedTouches[0].clientX - this.#touchStartX;
    const dy = event.changedTouches[0].clientY - this.#touchStartY;

    if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return;

    const { tabs } = this.refs;
    if (!tabs) return;
    const current = this.#activeIndex();

    if (dx < 0 && current < tabs.length - 1) {
      this.#activate(current + 1);
    } else if (dx > 0 && current > 0) {
      this.#activate(current - 1);
    }
  };

  #handleKeydown = (event) => {
    if (!event.target?.matches?.('[role="tab"]')) return;
    const { tabs } = this.refs;
    if (!tabs) return;

    const i = tabs.indexOf(event.target);
    let next = -1;

    switch (event.key) {
      case 'ArrowRight':
        next = (i + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (i - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    tabs[next]?.focus();
    this.#activate(next);
  };
}

if (!customElements.get('tabbed-content-component')) {
  customElements.define('tabbed-content-component', TabbedContent);
}
